// src/components/workloads/creation/WorkloadCreationWizard.tsx
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Map,
  Upload,
  Grid3X3,
  ChevronRight,
  ChevronLeft,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import { useWorkloadStore } from '../../../store/workloadStore';
import { useAuth } from '../../../contexts/AuthContext';
import { workloadService } from '../../../services/workload.service';
import { addressService } from '../../../services/address.service';
import toast from 'react-hot-toast';

// Step components
import TypeSelector from './TypeSelector';
import SaturationPointSelector from './SaturationPointSelector';
import DrawAreaSelector from './DrawAreaSelector';
import CsvUploadHandler from './CsvUploadHandler';
import CensusBlockSelector from './CensusBlockSelector';
import AddressListBuilder from './AddressListBuilder';
import WorkloadNamingStep from './WorkloadNamingStep';
import WorkloadReviewStep from './WorkloadReviewStep';

interface WizardStep {
  id: string;
  title: string;
  description: string;
  component: React.ReactNode;
  isValid: () => boolean;
}

const WorkloadCreationWizard: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const {
    creation,
    setCreationStep,
    setCreationType,
    resetCreation,
  } = useWorkloadStore();

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Define steps based on selected type
  const getSteps = useCallback((): WizardStep[] => {
    const baseSteps: WizardStep[] = [
      {
        id: 'type',
        title: 'Select Type',
        description: 'Choose how you want to create workloads',
        component: <TypeSelector />,
        isValid: () => creation.type !== null,
      },
    ];

    // Type-specific steps
    if (creation.type === 'saturation') {
      baseSteps.push({
        id: 'territory',
        title: 'Select Area',
        description: 'Draw a polygon or set a radius on the map',
        component: <SaturationPointSelector />,
        isValid: () =>
          (creation.territory.polygon !== null && creation.territory.polygon.length >= 3) ||
          (creation.territory.center !== null && creation.territory.radius > 0),
      });
    }

    if (creation.type === 'draw_area') {
      baseSteps.push({
        id: 'draw_area',
        title: 'Draw Area',
        description: 'Draw polygons on the map and pull addresses inside',
        component: <DrawAreaSelector />,
        isValid: () => creation.addresses.length > 0,
      });
    }

    if (creation.type === 'csv_upload') {
      baseSteps.push({
        id: 'upload',
        title: 'Upload CSV',
        description: 'Upload and map your address file',
        component: <CsvUploadHandler />,
        isValid: () => creation.addresses.length > 0,
      });
    }

    if (creation.type === 'census_block') {
      baseSteps.push({
        id: 'census',
        title: 'Select Blocks',
        description: 'Choose census blocks to target',
        component: <CensusBlockSelector />,
        isValid: () => creation.addresses.length > 0,
      });
    }

    // Common steps
    baseSteps.push(
      {
        id: 'addresses',
        title: 'Build Lists',
        description: 'Configure address lists and splitting',
        component: <AddressListBuilder />,
        isValid: () => creation.addresses.filter((a) => a.selected).length > 0,
      },
      {
        id: 'naming',
        title: 'Name & Assign',
        description: 'Name your workloads and assign users',
        component: <WorkloadNamingStep />,
        isValid: () => creation.name.trim() !== '' && creation.projectId !== '',
      },
      {
        id: 'review',
        title: 'Review',
        description: 'Review and create your workloads',
        component: <WorkloadReviewStep />,
        isValid: () => true,
      }
    );

    return baseSteps;
  }, [creation]);

  const steps = getSteps();
  const currentStep = steps[creation.step];
  const isFirstStep = creation.step === 0;
  const isLastStep = creation.step === steps.length - 1;
  const canProceed = currentStep?.isValid() ?? false;

  const handleNext = useCallback(() => {
    if (canProceed && !isLastStep) {
      setCreationStep(creation.step + 1);
    }
  }, [canProceed, isLastStep, creation.step, setCreationStep]);

  const handleBack = useCallback(() => {
    if (!isFirstStep) {
      setCreationStep(creation.step - 1);
    }
  }, [isFirstStep, creation.step, setCreationStep]);

  const handleCancel = useCallback(() => {
    if (window.confirm('Are you sure you want to cancel? All progress will be lost.')) {
      resetCreation();
      navigate('/workloads/manage');
    }
  }, [resetCreation, navigate]);

  const handleSubmit = useCallback(async () => {
    if (!currentUser || !userProfile) {
      toast.error('You must be logged in to create workloads');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get addresses from store
      const selectedAddresses = creation.addresses.filter((a) => a.selected);

      if (selectedAddresses.length === 0) {
        toast.error('Please select at least one address');
        setIsSubmitting(false);
        return;
      }

      // Determine if we need to split into multiple workloads
      const { enabled: splitEnabled, maxSize } = creation.listSplitting;
      const workloadsToCreate: typeof selectedAddresses[] = [];

      if (splitEnabled && selectedAddresses.length > maxSize) {
        // Split into chunks
        for (let i = 0; i < selectedAddresses.length; i += maxSize) {
          workloadsToCreate.push(selectedAddresses.slice(i, i + maxSize));
        }
      } else {
        workloadsToCreate.push(selectedAddresses);
      }

      // Create workloads
      const createdWorkloads: string[] = [];

      for (let i = 0; i < workloadsToCreate.length; i++) {
        const addressChunk = workloadsToCreate[i];
        const workloadName =
          workloadsToCreate.length > 1
            ? `${creation.name} - ${creation.listSplitting.namePrefix} ${i + 1}`
            : creation.name;

        // Create the workload
        console.log('>>> CREATING WORKLOAD <<<');
        console.log('Name:', workloadName);
        console.log('Assigned To:', creation.assignedTo);
        console.log('Assigned To Names:', creation.assignedToNames);

        const workload = await workloadService.create(
          {
            name: workloadName,
            projectId: creation.projectId,
            type: creation.type!,
            surveyId: creation.surveyId || undefined,
            surveyName: creation.surveyName || undefined,
            assignedTo: creation.assignedTo || [],
            assignedToNames: creation.assignedToNames || [],
            assignedGroups: creation.assignedGroups || [],
            territory: {
              name: workloadName,
              type: creation.territory.type || 'polygon',
              polygon: creation.territory.polygon
                ? {
                    coordinates: creation.territory.polygon,
                  }
                : undefined,
              centerPoint: creation.territory.center || undefined,
              radiusMeters: creation.territory.radius,
              bounds: calculateBounds(addressChunk),
            },
          },
          currentUser.uid,
          userProfile.displayName || userProfile.email || 'Unknown'
        );

        // Validate addresses have required fields
        const validAddresses = addressChunk.filter((addr) => {
          const hasStreet = addr.street && addr.street.trim().length > 0;
          const hasLocation = addr.location &&
            typeof addr.location.lat === 'number' &&
            typeof addr.location.lng === 'number' &&
            !isNaN(addr.location.lat) &&
            !isNaN(addr.location.lng);
          return hasStreet && hasLocation;
        });

        if (validAddresses.length === 0) {
          createdWorkloads.push(workload.id);
          continue;
        }

        // Prepare addresses for batch create
        const addressesToCreate = validAddresses.map((addr) => ({
          street: addr.street,
          unit: addr.unit || '',
          city: addr.city || '',
          state: addr.state || '',
          zipCode: addr.zipCode || '',
          location: {
            lat: addr.location.lat,
            lng: addr.location.lng,
          },
        }));

        const { created, errors } = await addressService.batchCreate(
          workload.id,
          addressesToCreate
        );

        if (errors.length > 0) {
          toast.error(`Warning: ${errors.length} addresses failed to save`);
        }

        // Verify addresses were actually saved
        const savedAddresses = await addressService.getWorkloadAddresses(workload.id);

        if (savedAddresses.length === 0) {
          toast.error('Failed to save addresses - please try again');
        }

        createdWorkloads.push(workload.id);
      }

      toast.success(
        `Created ${createdWorkloads.length} workload${createdWorkloads.length > 1 ? 's' : ''} with ${selectedAddresses.length} addresses`
      );

      resetCreation();
      navigate('/workloads/manage');
    } catch (error: any) {
      console.error('Error creating workloads:', error);
      toast.error(error.message || 'Failed to create workloads');
    } finally {
      setIsSubmitting(false);
    }
  }, [creation, currentUser, userProfile, resetCreation, navigate]);

  // Calculate bounds from addresses
  const calculateBounds = (addresses: typeof creation.addresses) => {
    if (addresses.length === 0) {
      return { north: 0, south: 0, east: 0, west: 0 };
    }

    let north = -Infinity;
    let south = Infinity;
    let east = -Infinity;
    let west = Infinity;

    addresses.forEach((addr) => {
      north = Math.max(north, addr.location.lat);
      south = Math.min(south, addr.location.lat);
      east = Math.max(east, addr.location.lng);
      west = Math.min(west, addr.location.lng);
    });

    return { north, south, east, west };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Workloads</h1>
            <p className="text-gray-600 mt-1">
              Step {creation.step + 1} of {steps.length}: {currentStep?.title}
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div
                  className={`flex items-center ${
                    index <= creation.step ? 'text-indigo-600' : 'text-gray-400'
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 ${
                      index < creation.step
                        ? 'bg-indigo-600 border-indigo-600 text-white'
                        : index === creation.step
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-gray-300 text-gray-400'
                    }`}
                  >
                    {index < creation.step ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span className="ml-2 text-sm font-medium hidden sm:block">
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-4 ${
                      index < creation.step ? 'bg-indigo-600' : 'bg-gray-300'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {currentStep?.component}
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={isFirstStep}
            className={`flex items-center px-4 py-2 rounded-md ${
              isFirstStep
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="h-5 w-5 mr-1" />
            Back
          </button>

          <div className="text-sm text-gray-500">
            {creation.addresses.filter((a) => a.selected).length} addresses selected
          </div>

          {isLastStep ? (
            <button
              onClick={handleSubmit}
              disabled={!canProceed || isSubmitting}
              className={`flex items-center px-6 py-2 rounded-md font-medium ${
                canProceed && !isSubmitting
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="h-5 w-5 mr-2" />
                  Create Workloads
                </>
              )}
            </button>
          ) : (
            <button
              onClick={handleNext}
              disabled={!canProceed}
              className={`flex items-center px-6 py-2 rounded-md font-medium ${
                canProceed
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Next
              <ChevronRight className="h-5 w-5 ml-1" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkloadCreationWizard;
